/**
 * Conversation API client utilities
 * Handles all conversation-related API calls
 */

import { getApiBaseUrl, authenticatedFetch } from './api';

/**
 * Map a loaded message's persisted `reply_context` (snake_case, see
 * .claude/plan/chatreply.md) onto `replyContext` (camelCase) -- the same
 * shape sendMessage stashes on a message for the live session in
 * ChatInterface.js -- so the reply caption renders identically whether the
 * message just arrived this session or was rehydrated after a reload.
 * Same for `focus_mode_switch` -> `focusModeSwitch` (the "Switched from ...
 * to ..." divider above the message whose turn changed focus mode).
 */
function normalizeLoadedMessages(messages) {
  if (!Array.isArray(messages)) return messages;
  return messages.map((msg) => {
    if (!msg) return msg;
    let out = msg;
    if (msg.reply_context && !msg.replyContext) {
      const rc = msg.reply_context;
      out = {
        ...out,
        replyContext: {
          text: rc.text,
          messageId: rc.quoted_message_id,
          role: rc.role,
          isFullMessage: !!rc.is_full_message,
        },
      };
    }
    if (msg.focus_mode_switch && !msg.focusModeSwitch) {
      out = { ...out, focusModeSwitch: msg.focus_mode_switch };
    }
    return out;
  });
}

/**
 * Create a new conversation
 * @param {string} name - Conversation name
 * @param {string} modelId - Model ID for this conversation
 * @returns {Promise<Object>} Created conversation object
 */
export async function createConversation(name, modelId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      model_id: modelId,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to create conversation');
  }

  return response.json();
}

/**
 * List all conversations for the current user
 * @param {string} modelId - Optional model ID to filter by
 * @returns {Promise<Array>} List of conversations
 */
export async function listConversations(modelId = null) {
  const apiBase = getApiBaseUrl();
  const url = modelId 
    ? `${apiBase}/conversations?model_id=${encodeURIComponent(modelId)}`
    : `${apiBase}/conversations`;
  
  const response = await authenticatedFetch(url);

  if (!response.ok) {
    throw new Error('Failed to list conversations');
  }

  const data = await response.json();
  return data.conversations || [];
}

/**
 * Get a specific conversation
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Conversation object
 */
export async function getConversation(conversationId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}`);

  if (!response.ok) {
    throw new Error('Failed to get conversation');
  }

  return response.json();
}

/**
 * Update conversation name
 * @param {string} conversationId - Conversation ID
 * @param {string} newName - New conversation name
 * @returns {Promise<Object>} Updated conversation object
 */
export async function updateConversationName(conversationId, newName) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: newName,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to update conversation');
  }

  return response.json();
}

/**
 * Persist the model picked from the composer's model picker onto this
 * conversation, so reopening it (refresh, or from the sidebar) resumes on
 * the model last selected instead of the one it was created with.
 * @param {string} conversationId - Conversation ID
 * @param {string} modelId - Model ID to persist
 * @returns {Promise<Object>} Updated conversation
 */
export async function setConversationModel(conversationId, modelId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}/model`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model_id: modelId,
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to update conversation model');
  }

  return response.json();
}

/**
 * Delete a conversation
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export async function deleteConversation(conversationId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete conversation');
  }

  return response.json();
}

/**
 * Delete multiple conversations
 * @param {string[]} conversationIds - Conversation IDs to delete
 * @returns {Promise<void>}
 */
export async function deleteConversations(conversationIds) {
  const results = await Promise.allSettled(
    conversationIds.map((id) => deleteConversation(id))
  );
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length) {
    throw new Error(`Failed to delete ${failed.length} conversation(s)`);
  }
}

/**
 * Load conversation messages
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} Conversation and messages
 */
export async function loadConversation(conversationId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}/load`, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = new Error('Failed to load conversation');
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  return { ...data, messages: normalizeLoadedMessages(data.messages) };
}

/**
 * Share a conversation with one or more @kpf.com recipients
 * @param {string} conversationId - Conversation ID
 * @param {string[]} recipientEmails - List of @kpf.com email addresses
 * @returns {Promise<Object>} { share_id, recipient_emails, message }
 */
export async function shareConversation(conversationId, recipientEmails) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}/share`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ recipient_emails: recipientEmails }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to share conversation');
  }

  return response.json();
}

/**
 * Load a shared conversation (read-only). Only for recipients.
 * @param {string} shareId - Share ID from the link (?share=...)
 * @returns {Promise<Object>} { conversation, messages, shared, share_id }
 */
export async function loadSharedConversation(shareId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/shared/${shareId}`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(data.detail || 'Failed to load shared conversation');
    err.status = response.status;
    throw err;
  }

  const data = await response.json();
  return { ...data, messages: normalizeLoadedMessages(data.messages) };
}

/**
 * Import a shared conversation into the current user's list (copy; can continue).
 * @param {string} shareId - Share ID
 * @returns {Promise<Object>} { conversation_id, conversation }
 */
export async function importSharedConversation(shareId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/shared/${shareId}/import`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to import conversation');
  }

  return response.json();
}

/**
 * Create a new folder to organize conversations under.
 * @param {string} name - Folder name
 * @returns {Promise<Object>} Created folder object
 */
export async function createFolder(name) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/folders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to create folder');
  }

  return response.json();
}

/**
 * List all folders for the current user
 * @returns {Promise<Array>} List of folders
 */
export async function listFolders() {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/folders`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to list folders');
  }

  const data = await response.json();
  return data.folders || [];
}

/**
 * Rename a folder
 * @param {string} folderId - Folder ID
 * @param {string} newName - New folder name
 * @returns {Promise<Object>} Updated folder object
 */
export async function renameFolder(folderId, newName) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/folders/${folderId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name: newName }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to rename folder');
  }

  return response.json();
}

/**
 * Delete a folder. Conversations inside are unassigned (moved to Unfiled), not deleted.
 * @param {string} folderId - Folder ID
 * @returns {Promise<Object>} Deletion confirmation with unassigned_count
 */
export async function deleteFolder(folderId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/folders/${folderId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to delete folder');
  }

  return response.json();
}

/**
 * Move a conversation into a folder, or back to Unfiled.
 * @param {string} conversationId - Conversation ID
 * @param {string|null} folderId - Folder ID to file under, or null to unfile
 * @returns {Promise<Object>} Updated conversation object
 */
export async function setConversationFolder(conversationId, folderId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}/folder`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ folder_id: folderId }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to move conversation');
  }

  return response.json();
}

/**
 * Generate (or fetch cached) a handover summary for a conversation.
 * @param {string} conversationId - Conversation ID
 * @param {object} options - { force: boolean } to bypass cache
 * @returns {Promise<Object>} HandoverSummary JSON
 */
export async function generateHandoverSummary(conversationId, { force = false } = {}) {
  const apiBase = getApiBaseUrl();
  const url = `${apiBase}/conversations/${conversationId}/handover${force ? '?refresh=true' : ''}`;
  const response = await authenticatedFetch(url, { method: 'POST' });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to generate handover summary');
  }
  return response.json();
}

/**
 * Download a .docx file with the handover summary. The browser saves the file
 * via an object URL instead of navigating, so auth headers are sent correctly.
 * @param {string} conversationId
 * @param {string} filenameHint - optional filename (backend also sends one)
 * @param {'plain'|'letterhead'} format - 'plain' (default) or 'letterhead' (KPF letterhead template)
 */
export async function downloadHandoverDocx(conversationId, filenameHint, format = 'plain') {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/conversations/${conversationId}/handover.docx?format=${encodeURIComponent(format)}`,
    { method: 'GET', headers: {} } // empty headers object prevents json CT
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to download handover document');
  }

  // Prefer filename from Content-Disposition
  let filename = filenameHint || `Handover-${conversationId}.docx`;
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (match && match[1]) filename = decodeURIComponent(match[1]);

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
}

/**
 * Create a new conversation seeded with the handover summary from a source
 * conversation. The returned payload mirrors createConversation so the caller
 * can drop it into the sidebar and switch to it.
 * @param {string} conversationId - Source conversation ID
 * @param {{asGroup?: boolean}} [options] - from a group chat: a new group chat
 *   for the same people, rather than the caller's own conversation
 * @returns {Promise<Object>} { conversation_id, conversation }
 */
export async function continueFromHandover(conversationId, { asGroup = false } = {}) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/conversations/${conversationId}/handover/continue${asGroup ? '?as_group=true' : ''}`,
    { method: 'POST' }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to continue from handover');
  }
  return response.json();
}


// --- Projects -------------------------------------------------------------
//
// A project groups conversations like a folder does, and adds standing
// instructions plus a shared file library. See .claude/plan/plan_projects.md.

/**
 * Create a project workspace.
 * @param {string} name
 * @param {Object} [options]
 * @param {string} [options.instructions] - its free-text instructions,
 *   checked against KAI's policies first: a 422 (refused) or 503 (couldn't
 *   check) creates nothing.
 * @param {string[]} [options.defaultRuleIds] - the user's own custom_rules
 *   ids to always attach to every conversation in this project.
 * @returns {Promise<Object>} Created project. A failure's Error carries the
 *   HTTP `status`, and `field` ('name' / 'instructions') when the server's
 *   own validation names one, so the caller can show it by that field.
 */
export async function createProject(name, { instructions = '', defaultRuleIds = [] } = {}) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, instructions, default_rule_ids: defaultRuleIds }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    // A field that fails validation (too long, say) comes back as a list of
    // {loc, msg}, not a sentence.
    const issues = Array.isArray(data.detail) ? data.detail : null;
    const detail = issues
      ? issues.map((issue) => String(issue.msg || '').replace(/^Value error, /, '')).join(' ')
      : data.detail;
    const err = new Error(detail || 'Failed to create project');
    err.status = response.status;
    if (issues) err.field = issues[0]?.loc?.[1];
    throw err;
  }
  return response.json();
}

/**
 * List the current user's projects.
 * @returns {Promise<Array>}
 */
export async function listProjects() {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to list projects');
  }
  const data = await response.json();
  return data.projects || [];
}

/**
 * Get a single project by id.
 * @param {string} projectId
 * @returns {Promise<Object>}
 */
export async function getProject(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to load project');
  }
  return response.json();
}

/**
 * Update a project's name and/or default rule set. Both are optional and
 * independent, so the settings panel can save one without clobbering the others.
 * `instructions: ''` / `default_rule_ids: []` explicitly clears that field;
 * omitting a field leaves it untouched.
 * @param {string} projectId
 * @param {{name?: string, instructions?: string, default_rule_ids?: string[]}} fields
 * @returns {Promise<Object>} Updated project
 */
export async function updateProject(projectId, fields) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to update project');
  }
  return response.json();
}

/**
 * Move a project to the Recycle Bin. Its instructions, knowledge base, and
 * conversations are all left untouched -- they just become unreachable
 * (like the project itself) until restoreProject brings it back, or it sits
 * unrestored past the retention window and is purged automatically.
 *
 * A project others have joined goes to the bin for everyone in it, each
 * person's own conversations saved to a folder in their regular list, and
 * a restore brings it back for all of them. If its members changed while it
 * was being deleted, the server changes nothing and the error carries status
 * 409 -- reload the project and show the dialog again.
 * @param {string} projectId
 * @returns {Promise<Object>} { deleted_at, expires_at, days_remaining, members_removed, ... }
 */
export async function deleteProject(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw Object.assign(new Error(data.detail || 'Failed to delete project'), { status: response.status });
  }
  return response.json();
}

/**
 * The caller's Recycle Bin -- projects deleted but not yet purged.
 * @returns {Promise<Array>} [{ project_id, name, deleted_at, expires_at, days_remaining }]
 */
export async function listDeletedProjects() {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/deleted`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to load Recycle Bin');
  }
  const data = await response.json();
  return data.projects || [];
}

/**
 * Restore a project out of the Recycle Bin, exactly as it was.
 * @param {string} projectId
 * @returns {Promise<Object>} The restored project
 */
export async function restoreProject(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/restore`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to restore project');
  }
  return response.json();
}

/**
 * Skip the Recycle Bin and delete a project (active or already in the bin)
 * for good, right now. Its conversations are unfiled by default -- pass
 * deleteConversations=true to delete them outright instead.
 * @param {string} projectId
 * @param {boolean} [deleteConversations=false]
 * @returns {Promise<Object>} { index_objects_deleted, conversations_unassigned }
 *   or { index_objects_deleted, conversations_deleted } when deleteConversations is true.
 */
export async function permanentlyDeleteProject(projectId, deleteConversations = false) {
  const apiBase = getApiBaseUrl();
  const query = deleteConversations ? '?delete_conversations=true' : '';
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/permanent${query}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to permanently delete project');
  }
  return response.json();
}

/**
 * Move a conversation into a project, or out of one when projectId is null.
 * Also clears any folder assignment — the two are mutually exclusive.
 * @param {string} conversationId
 * @param {string|null} projectId
 * @returns {Promise<{conversation: object, note: {text: string, additional_data: object}|null}>}
 *   The updated conversation, and on entering a project the note now at the
 *   end of its transcript.
 */
export async function setConversationProject(conversationId, projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/conversations/${conversationId}/project`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to move conversation');
  }
  return response.json();
}

/**
 * Set the manual drag-to-reorder position of a project's own Chats list --
 * the full desired order, as conversation ids.
 * @param {string} projectId
 * @param {string[]} conversationIds
 */
export async function reorderProjectConversations(projectId, conversationIds) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/conversations/order`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversation_ids: conversationIds }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to reorder conversations');
  }
  return response.json();
}

/**
 * Restrict (or, with scope=null, clear back to "no explicit selection" --
 * the full knowledge base) which subset of this conversation's project
 * Knowledge base its retrieval may draw from. See
 * .claude/plan/plan_knowledge_scope.md.
 * @param {string} conversationId
 * @param {{mode: 'all'|'selected', file_ids: string[], folder_ids: string[]}|null} scope
 * @returns {Promise<{conversation: object, note: {text: string, additional_data: object}|null}>}
 */
export async function setConversationKnowledgeScope(conversationId, scope) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/conversations/${conversationId}/knowledge-scope`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(scope),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to update this conversation\'s knowledge-base scope');
  }
  return response.json();
}

/**
 * Project files someone else added after this conversation joined its
 * project, which it leaves out until the user adds them (empty outside a
 * project).
 * @param {string} conversationId
 * @returns {Promise<Array<{file_id: string, name: string, status: string, created_at: number,
 *   uploaded_by: string, uploaded_by_name: string|null, dismissed: boolean}>>}
 */
export async function listHeldFiles(conversationId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}/held-files`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to load new knowledge base files');
  }
  const data = await response.json();
  return data.held_files || [];
}

/**
 * "Use in this chat" (accept) or "Not now" (dismiss) for held files.
 * @param {string} conversationId
 * @param {{accept?: string[], dismiss?: string[]}} choices
 * @returns {Promise<{held_files: Array, note: {text: string, additional_data: object}|null}>}
 */
export async function answerHeldFiles(conversationId, { accept = [], dismiss = [] }) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/conversations/${conversationId}/held-files`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accept, dismiss }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to update this conversation\'s files');
  }
  return response.json();
}

/**
 * The indexed documents in a project's file library.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function listProjectFiles(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/files`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to list project files');
  }
  const data = await response.json();
  return data.files || [];
}

/**
 * A short-lived presigned URL to a standalone image upload's own image, for
 * the Knowledge base file list's thumbnail preview. null if the file has no
 * stored image (true of most documents).
 * @param {string} projectId
 * @param {string} fileId
 * @returns {Promise<string|null>}
 */
export async function getProjectFileThumbnailUrl(projectId, fileId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/files/${fileId}/thumbnail`);
  if (!response.ok) return null;
  const data = await response.json().catch(() => ({}));
  return data.url || null;
}

/**
 * The eye-icon preview for one Knowledge base file: a URL to view it inline
 * plus one to force a download, or null if this file has no original bytes
 * to show (true of every format except a real PDF or a standalone image).
 * @param {string} projectId
 * @param {string} fileId
 * @returns {Promise<{kind: 'pdf'|'image', name: string, view_url: string, download_url: string}|null>}
 */
export async function getProjectFilePreview(projectId, fileId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/files/${fileId}/preview`);
  if (!response.ok) return null;
  return response.json().catch(() => null);
}

/**
 * One page of an indexed PDF drawn as an image, for the chat's "view page N"
 * preview -- quick, where opening the PDF itself downloads all of it.
 * @param {string} fileId
 * @param {number} page
 * @param {string|null} projectId the project the file is in, if any
 * @returns {Promise<{url: string, page: number, name: string}|null>}
 */
export async function getPdfPageDrawing(fileId, page, projectId) {
  const apiBase = getApiBaseUrl();
  const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  const response = await authenticatedFetch(
    `${apiBase}/pdf-pages/${encodeURIComponent(fileId)}/${page}${query}`
  );
  if (!response.ok) return null;
  return response.json().catch(() => null);
}

/**
 * Rename a document in a project's Knowledge base -- cosmetic only, the
 * file_id and its search index are untouched.
 * @param {string} projectId
 * @param {string} fileId
 * @param {string} newName
 */
export async function renameProjectFile(projectId, fileId, newName) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/files/${fileId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to rename file');
  }
  return response.json();
}

/**
 * Remove one document from a project's library, index and all.
 * @param {string} projectId
 * @param {string} fileId
 */
export async function deleteProjectFile(projectId, fileId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/files/${fileId}`,
    { method: 'DELETE' }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to remove file');
  }
  return response.json();
}

/**
 * Add a new folder to a project's Knowledge base.
 * @param {string} projectId
 * @param {string} name
 * @returns {Promise<Object>} The updated project (includes the new file_folders list)
 */
export async function createProjectFileFolder(projectId, name) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/file-folders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to create folder');
  }
  return response.json();
}

/**
 * Rename one of a project's Knowledge base folders.
 * @param {string} projectId
 * @param {string} folderId
 * @param {string} newName
 * @returns {Promise<Object>} The updated project
 */
export async function renameProjectFileFolder(projectId, folderId, newName) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/file-folders/${folderId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to rename folder');
  }
  return response.json();
}

/**
 * Delete a Knowledge base folder. Files inside are unfiled, not deleted.
 * @param {string} projectId
 * @param {string} folderId
 * @returns {Promise<Object>} { project, files_unassigned }
 */
export async function deleteProjectFileFolder(projectId, folderId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/file-folders/${folderId}`,
    { method: 'DELETE' }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to delete folder');
  }
  return response.json();
}

/**
 * File (or unfile) one project document under a Knowledge base folder --
 * the drag-and-drop / "Move to folder" menu target.
 * @param {string} projectId
 * @param {string} fileId
 * @param {string|null} folderId - Folder id to file under, or null to unfile
 */
export async function setProjectFileFolder(projectId, fileId, folderId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/files/${fileId}/folder`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder_id: folderId }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to move file');
  }
  return response.json();
}

/**
 * Add a new folder to a project's Conversation history tab.
 * @param {string} projectId
 * @param {string} name
 * @returns {Promise<Object>} The updated project (includes the new conversation_folders list)
 */
export async function createProjectConversationFolder(projectId, name) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/conversation-folders`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to create folder');
  }
  return response.json();
}

/**
 * Rename one of a project's Conversation history folders.
 * @param {string} projectId
 * @param {string} folderId
 * @param {string} newName
 * @returns {Promise<Object>} The updated project
 */
export async function renameProjectConversationFolder(projectId, folderId, newName) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/conversation-folders/${folderId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to rename folder');
  }
  return response.json();
}

/**
 * Delete a Conversation history folder. Conversations inside are unfiled, not deleted.
 * @param {string} projectId
 * @param {string} folderId
 * @returns {Promise<Object>} { project, conversations_unassigned }
 */
export async function deleteProjectConversationFolder(projectId, folderId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/conversation-folders/${folderId}`,
    { method: 'DELETE' }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to delete folder');
  }
  return response.json();
}

/**
 * File (or unfile) one project conversation under a Conversation history
 * folder -- the drag-and-drop / "Move to folder" menu target.
 * @param {string} projectId
 * @param {string} conversationId
 * @param {string|null} folderId - Folder id to file under, or null to unfile
 */
export async function setProjectConversationFolder(projectId, conversationId, folderId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/conversations/${conversationId}/folder`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder_id: folderId }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to move conversation');
  }
  return response.json();
}

/**
 * Everyone with access to a project, including pending (not-yet-logged-in)
 * invites -- see backend/services/project_membership_service.py.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function listProjectMembers(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/members`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to load project members');
  }
  const data = await response.json();
  return data.members || [];
}

/**
 * Invite a KPF colleague to a project by email, as 'view' or 'edit' access.
 * The creator or an Edit member. Always creates a pending invite -- there's
 * no local user directory, so even someone who has used KAI before can't be
 * resolved to an id at invite time; it grants nothing until they accept it.
 * @param {string} projectId
 * @param {string} email
 * @param {string} [role='view']
 * @param {Object} [options]
 * @param {string} [options.conversationId] - Invited from this group chat's
 *   header: the email links straight to it.
 */
export async function inviteProjectMember(projectId, email, role = 'view', { conversationId } = {}) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(conversationId ? { email, role, conversation_id: conversationId } : { email, role }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to send invite');
  }
  return response.json();
}

/**
 * Autocomplete suggestions for the "type a colleague's email" boxes (project
 * invite, conversation share): @kpf.com colleagues matching `query` by name
 * or email in the KPF employee directory, plus people who have signed into
 * KAI before as a fallback -- see the /projects/users/search route (named for
 * where it started; it only needs a signed-in user). Returns [] for anything
 * under 2 characters without making a request. Used through
 * useKpfSuggestions in components/KpfEmailSuggestions.js.
 * @param {string} query
 * @returns {Promise<Array<{email: string, display_name: string|null, profile_img: string|null}>>}
 */
export async function searchKpfUsers(query) {
  const trimmed = (query || '').trim();
  if (trimmed.length < 2) return [];
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/users/search?q=${encodeURIComponent(trimmed)}`);

  if (!response.ok) return [];
  const data = await response.json().catch(() => ({}));
  return data.users || [];
}

/**
 * Project invitations waiting for the current user's Accept/Decline. An
 * invitation grants nothing until accepted -- the project does not appear in
 * listProjects() and getProject() on it 404s -- so this is the only place a
 * not-yet-accepted project is visible.
 * @returns {Promise<Array<{project_id: string, project_name: string, role: 'view'|'edit',
 *   invited_by_email: string|null, invited_by_name: string|null,
 *   invited_by_profile_img: string|null, invited_at: string|null}>>}
 */
export async function listProjectInvitations() {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/invitations`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to load invitations');
  }
  const data = await response.json();
  return data.invitations || [];
}

/**
 * Accept a pending project invitation.
 *
 * For someone who left this project before, `restoreConversations` brings
 * their own conversations from it (saved in a "Project-<name>" folder when
 * they left) back into the project; without it they stay where they are as
 * ordinary chats and are not offered again.
 * @param {string} projectId
 * @param {{restoreConversations?: boolean}} [options]
 * @returns {Promise<Object>} The now-shared project (my_role, shared, unseen set;
 *   restored_conversation_count says how many came back, or null)
 */
export async function acceptProjectInvitation(projectId, { restoreConversations = false } = {}) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/invitations/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restore_conversations: restoreConversations }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to accept this invitation');
  }
  return response.json();
}

/**
 * Decline a pending project invitation. The project never appears for the
 * user; the owner can invite them again later.
 * @param {string} projectId
 */
export async function declineProjectInvitation(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/invitations/decline`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to decline this invitation');
  }
  return response.json();
}

/**
 * What a project contains, for the invitee to look at before accepting: the
 * instructions text, knowledge-base folder names and file names/sizes. Names
 * only -- no file contents, previews or search until the invitation is
 * accepted. 404s if the caller has no pending invitation to this project.
 * @param {string} projectId
 * @returns {Promise<{project_id: string, project_name: string, role: string,
 *   instructions: string, owner_name: ?string, owner_email: ?string,
 *   folders: string[], files: {name: string, size: number, folder_name: ?string}[],
 *   file_count: number}>}
 */
export async function getProjectInvitationPreview(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/invitation-preview`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to load this project preview');
  }
  return response.json();
}

/**
 * Change an existing member's access level between 'view' and 'edit'.
 * The creator or an Edit member -- the creator's own access cannot be
 * changed this way.
 * @param {string} projectId
 * @param {string} userId
 * @param {string} role
 */
export async function updateProjectMemberRole(projectId, userId, role) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/members/${encodeURIComponent(userId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || "Failed to update this member's role");
  }
  return response.json();
}

/**
 * Remove a member (or a pending invite) from a project. The creator or an
 * Edit member -- nobody can remove the creator, and nobody removes
 * themselves this way (that's leaving).
 * @param {string} projectId
 * @param {string} userId
 */
export async function removeProjectMember(projectId, userId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(
    `${apiBase}/projects/${projectId}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to remove this member');
  }
  return response.json();
}

/**
 * Leave a project you were given access to. The creator cannot leave
 * their own project this way -- the backend rejects with a 409.
 *
 * YOUR OWN conversations in that project (nobody else's are touched) are
 * always saved, in a "Project-<name>" folder among your regular conversations
 * -- there is no way to delete them from here. They never go back into the
 * project.
 * @param {string} projectId
 * @returns {Promise<{success: boolean, conversations_saved: number,
 *   folder_name: ?string}>}
 */
export async function leaveProject(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/leave`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to leave this project');
  }
  return response.json();
}

/**
 * Clear the unread badge for a project shared with the current user -- call
 * once when opening a project you don't own. A no-op for your own project
 * or an already-seen one.
 * @param {string} projectId
 */
export async function markProjectSeen(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/seen`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to mark this project seen');
  }
  return response.json();
}

/**
 * Record that the current user just looked at a project's Knowledge base, so
 * files other members added before now stop coming back with is_new. A no-op
 * for a project that was never shared.
 * @param {string} projectId
 */
export async function markProjectFilesSeen(projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/projects/${projectId}/files/seen`, {
    method: 'POST',
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to mark this project\'s files seen');
  }
  return response.json();
}

// ── Group chats ─────────────────────────────────────────────────────────────
// One conversation every member of a shared project can read and ask KAI in
// (see .claude/plan/plan_multi-user-conversation.md).

async function groupChatRequest(path, { method = 'GET', body, failure } = {}) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}${path}`, {
    method,
    ...(body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const error = new Error(data.detail || failure);
    error.status = response.status;
    throw error;
  }
  return response.json();
}

/**
 * A project's group chats, most recently active first, each with whether
 * the caller has unread messages in it.
 * @param {string} projectId
 * @returns {Promise<Array>}
 */
export async function listProjectGroupChats(projectId) {
  const data = await groupChatRequest(`/projects/${projectId}/group-chats`, {
    failure: "Couldn't load this project's group chats",
  });
  return data.group_chats || [];
}

/**
 * Start a new, empty group chat in a project.
 * @param {string} projectId
 * @param {Object} [options]
 * @param {string} [options.name]
 * @param {string} [options.modelId]
 * @param {string[]|null} [options.memberIds] - The project members (user ids)
 *   it's limited to, besides you; null or omitted for everyone in the project.
 * @returns {Promise<Object>} The conversation, with its `group` details.
 */
export async function createGroupChat(projectId, { name, modelId, memberIds = null } = {}) {
  return groupChatRequest(`/projects/${projectId}/group-chats`, {
    method: 'POST',
    body: { name: name || null, model_id: modelId || null, member_ids: memberIds },
    failure: "Couldn't start the group chat",
  });
}

/**
 * Make one of your own conversations in a project a group chat. One-way.
 * @param {string} projectId
 * @param {string} conversationId
 * @param {Object} [options]
 * @param {string[]|null} [options.memberIds] - As for createGroupChat.
 * @returns {Promise<Object>} The conversation, with its `group` details.
 */
export async function promoteConversationToGroup(projectId, conversationId, { memberIds = null } = {}) {
  return groupChatRequest(`/projects/${projectId}/conversations/${conversationId}/group`, {
    method: 'POST',
    body: { member_ids: memberIds },
    failure: "Couldn't make this a group chat",
  });
}

/**
 * Change who's in a group chat -- its starter or the project's owner only.
 * @param {string} conversationId
 * @param {string[]|null} memberIds - The project members it's limited to, or
 *   null to open it to everyone in the project.
 * @returns {Promise<Object>} `{ group }`: the chat's details as they now stand.
 */
export async function setGroupChatMembers(conversationId, memberIds) {
  return groupChatRequest(`/conversations/${conversationId}/group/members`, {
    method: 'PUT',
    body: { member_ids: memberIds },
    failure: "Couldn't change who's in this group chat",
  });
}

/**
 * Add back someone who's rejoined the project since leaving it, or being
 * removed from it -- its starter or the project's owner only. A note in the
 * chat says who did.
 * @param {string} conversationId
 * @param {string} userId
 * @returns {Promise<Object>} `{ group }`: the chat's details as they now stand.
 */
export async function addBackToGroupChat(conversationId, userId) {
  return groupChatRequest(`/conversations/${conversationId}/group/add-back`, {
    method: 'POST',
    body: { user_id: userId },
    failure: "Couldn't add them back to this group chat",
  });
}

/**
 * Change the focus mode a group chat answers everyone in -- anyone in it, to
 * a mode they can use. A note in the chat says who did.
 * @param {string} conversationId
 * @param {string} focusMode
 * @returns {Promise<Object>} `{ group }`: the chat's details as they now stand.
 */
export async function setGroupChatFocusMode(conversationId, focusMode) {
  return groupChatRequest(`/conversations/${conversationId}/group/focus-mode`, {
    method: 'PUT',
    body: { focus_mode: focusMode },
    failure: "Couldn't change this chat's focus mode",
  });
}

/**
 * Rename a group chat (its starter, or the project's owner or Edit members).
 * @param {string} conversationId
 * @param {string} name
 * @returns {Promise<Object>} The conversation.
 */
export async function renameGroupChat(conversationId, name) {
  return groupChatRequest(`/conversations/${conversationId}`, {
    method: 'PUT',
    body: { name },
    failure: "Couldn't rename this group chat",
  });
}

/**
 * Delete a group chat for yourself -- anyone in it: you leave it, keeping no
 * copy, and it carries on for everyone else (the last one in it takes the
 * chat with them). Refused (409) while KAI is answering in it.
 * @param {string} conversationId
 * @returns {Promise<Object>}
 */
export async function deleteGroupChat(conversationId) {
  return groupChatRequest(`/conversations/${conversationId}`, {
    method: 'DELETE',
    failure: "Couldn't delete this group chat",
  });
}

/**
 * Leave a group chat -- anyone in it, its starter too -- keeping a read-only
 * copy. A note in it tells everyone still there. Refused (409) while KAI is
 * answering in it.
 * @param {string} conversationId
 * @returns {Promise<Object>}
 */
export async function leaveGroupChat(conversationId) {
  return groupChatRequest(`/conversations/${conversationId}/group/leave`, {
    method: 'POST',
    failure: "Couldn't leave this group chat",
  });
}

/**
 * For the person a group chat was copied from (their shared conversation,
 * imported and made a group chat by someone else): their own conversation,
 * who made the chat, and whether theirs has gone on since. 404 when there's
 * nothing to ask.
 * @param {string} conversationId - the group chat's
 */
export async function getGroupChatOriginalCopy(conversationId) {
  return groupChatRequest(`/conversations/${conversationId}/original-copy`, {
    failure: "Couldn't check your own copy of this conversation",
  });
}

/**
 * Keep your own conversation as well as the group chat made from a copy of
 * it -- you aren't asked again.
 * @param {string} conversationId - the group chat's
 */
export async function keepGroupChatOriginalCopy(conversationId) {
  return groupChatRequest(`/conversations/${conversationId}/original-copy/keep`, {
    method: 'POST',
    failure: "Couldn't keep your copy",
  });
}

/**
 * The caller has just looked at a project's group chats: its unread dot goes.
 * @param {string} projectId
 */
export async function markProjectGroupChatsSeen(projectId) {
  return groupChatRequest(`/projects/${projectId}/group-chats/seen`, {
    method: 'POST',
    failure: "Couldn't mark the group chats read",
  });
}

/**
 * What changed in an open group chat since the last poll -- one small read
 * when nothing did. See group_chat_service.activity_snapshot for the shape.
 * @param {string} conversationId
 * @param {Object} since
 * @param {string} [since.projectId]
 * @param {number} [since.sinceSeq]
 * @param {number} [since.sinceIndex]
 * @param {number} [since.historyVersion]
 * @param {boolean} [since.seen] - On screen and focused: counts as read.
 */
export async function pollGroupChatActivity(conversationId, {
  projectId, sinceSeq, sinceIndex, historyVersion, seen = false,
} = {}) {
  const params = new URLSearchParams();
  if (projectId) params.set('project_id', projectId);
  if (sinceSeq !== undefined && sinceSeq !== null) params.set('since_seq', String(sinceSeq));
  if (sinceIndex !== undefined && sinceIndex !== null) params.set('since_index', String(sinceIndex));
  if (historyVersion !== undefined && historyVersion !== null) params.set('history_version', String(historyVersion));
  if (seen) params.set('seen', 'true');
  const data = await groupChatRequest(`/conversations/${conversationId}/activity?${params}`, {
    failure: "Couldn't check the group chat for new messages",
  });
  return data.messages ? { ...data, messages: normalizeLoadedMessages(data.messages) } : data;
}

/**
 * Tell everyone else in a group chat that you're typing (or have stopped).
 * @param {string} conversationId
 * @param {{projectId?: string, typing?: boolean}} options
 */
export async function setGroupChatTyping(conversationId, { projectId, typing = true } = {}) {
  const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  return groupChatRequest(`/conversations/${conversationId}/typing${query}`, {
    method: 'POST',
    body: { typing },
    failure: "Couldn't tell the group chat you're typing",
  });
}

/**
 * Take back your own question waiting in a group chat's queue (or clear one
 * that was never sent).
 * @param {string} conversationId
 * @param {string} pendingId
 */
export async function cancelGroupChatQuestion(conversationId, pendingId) {
  return groupChatRequest(`/conversations/${conversationId}/pending/${pendingId}`, {
    method: 'DELETE',
    failure: "Couldn't take that question back",
  });
}

/**
 * The current user's custom rules -- reused by the project settings panel to
 * offer as "always attach in this project" checkboxes, the same rules the
 * Custom Rules panel manages.
 * @returns {Promise<Array>}
 */
export async function listCustomRules() {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/custom-rules`);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to load your rules');
  }
  const data = await response.json();
  return data.rules || [];
}

/**
 * Check whether attaching candidateRuleId alongside alreadyAttachedRuleIds
 * (and, if projectId is given, that project's own free-text instructions --
 * see ProjectPanel.js's Instructions tab) would put two contradictory
 * instructions in play at once. Purely id-based otherwise (no conversation
 * id needed), so it works before a conversation exists -- see
 * ChatInterface.js's per-conversation attach popover for the original use.
 * A conflict against the project's instructions comes back with
 * `conflicting_rule_id: '__project_instructions__'` instead of a real rule
 * id, so callers can phrase the block accordingly.
 * @param {string} candidateRuleId
 * @param {string[]} alreadyAttachedRuleIds
 * @param {string} [projectId]
 * @returns {Promise<{conflict: boolean, conflicting_rule_id?: string, conflicting_rule_title?: string, reason?: string}>}
 */
export async function checkRuleAttachConflict(candidateRuleId, alreadyAttachedRuleIds, projectId) {
  const apiBase = getApiBaseUrl();
  const response = await authenticatedFetch(`${apiBase}/custom-rules/check-attach-conflict`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      candidate_rule_id: candidateRuleId,
      already_attached_rule_ids: alreadyAttachedRuleIds,
      project_id: projectId || undefined,
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Could not check for rule conflicts');
  }
  return response.json();
}

// Upload polling cadence for a project-library upload. Matches
// JOB_POLL_INTERVAL_MS / JOB_POLL_CEILING_MS in ChatInterface.js -- not
// imported from there since that file doesn't export them, and this is a
// small enough constant to duplicate rather than restructure that component.
const PROJECT_UPLOAD_POLL_INTERVAL_MS = 5000;
const PROJECT_UPLOAD_POLL_CEILING_MS = 30 * 60 * 1000;

// Mirrors backend config.PDF_INDEX_GENERIC_EXTENSIONS plus .pdf itself (same
// set ChatInterface.js's isIndexableDocument checks) -- the formats eligible
// for the direct-to-S3 path below, since those are the ones a project's
// knowledge base promises to index regardless of size.
const DIRECT_UPLOAD_EXTENSIONS = ['.pdf', '.txt', '.md', '.markdown', '.csv', '.docx', '.xlsx', '.pptx', '.eml', '.msg'];
const isEligibleForDirectUpload = (file) => (
  DIRECT_UPLOAD_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))
);

/**
 * A direct-upload failure the proxied fallback would only repeat: the upload
 * was cancelled, or the server turned down this user or this file (no Edit
 * access, unreadable, ...). Sending the file again the proxied way would
 * index a cancelled upload anyway, or upload a refused file twice.
 *
 * @param {string} message
 * @returns {Error} flagged `noFallback`
 */
export function finalUploadError(message) {
  return Object.assign(new Error(message), { noFallback: true });
}

/**
 * Sends the file body straight to S3 via a presigned POST, so a huge drawing
 * set or spec book never passes through the ALB/Apache/gunicorn app tier (and
 * its LimitRequestBody ceiling) at all -- the proxied path below still
 * buffers the whole body through our own backend first, which is fine for a
 * small file but not what "no size limit" should mean for a large one. Falls
 * through to the caller's own proxied fallback when the direct path itself
 * fails (disabled, CORS not yet configured, network issue, etc), but not when
 * the server refused the user or the file (see finalUploadError).
 *
 * @returns {Promise<string>} the job id, to hand to the shared poll loop below
 */
async function uploadProjectFileDirect(apiBase, projectId, file) {
  const initResponse = await authenticatedFetch(`${apiBase}/upload-file/document-init`, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, project_id: projectId }),
  });
  if (initResponse.status === 403 || initResponse.status === 404) {
    const data = await initResponse.json().catch(() => ({}));
    throw finalUploadError(data.detail || `Upload failed (status ${initResponse.status})`);
  }
  if (!initResponse.ok) {
    throw new Error(`Direct upload unavailable (status ${initResponse.status})`);
  }
  const { job_id: jobId, upload_url: uploadUrl, fields } = await initResponse.json();

  try {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append('file', file); // must be the last field per S3's POST policy rules
    const uploadResponse = await fetch(uploadUrl, { method: 'POST', body: formData });
    if (!uploadResponse.ok) {
      throw new Error(`S3 upload failed (status ${uploadResponse.status})`);
    }
  } catch (error) {
    await cancelDirectDocumentUpload(jobId, projectId);
    throw error;
  }
  await startDirectDocumentIndexing(apiBase, jobId, projectId);
  return jobId;
}

/**
 * Starts indexing a document the browser has already put in S3. The file is
 * never sent again from here: a lost reply or a server error is asked again
 * (the server answers a repeat with the same job), and a failure is final,
 * never a fallback to the proxied upload.
 *
 * @param {string} apiBase
 * @param {string} jobId - from /upload-file/document-init
 * @param {string|null} projectId
 */
export async function startDirectDocumentIndexing(apiBase, jobId, projectId) {
  const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  const url = `${apiBase}/upload-file/document-start/${jobId}${query}`;
  let lastError = null;
  for (const delay of [0, 1000, 3000, 8000]) {
    if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
    let response;
    try {
      response = await authenticatedFetch(url, { method: 'POST' });
    } catch (error) {
      lastError = error;
      continue;
    }
    if (response.ok) return;
    const data = await response.json().catch(() => ({}));
    const message = data.detail || `Could not start indexing (status ${response.status})`;
    // A 4xx is the server's verdict on this user or file.
    if (response.status < 500) throw finalUploadError(message);
    lastError = new Error(message);
  }
  throw finalUploadError(
    `The file was uploaded, but indexing couldn't be started (${lastError?.message || 'no response'}). Try adding it again.`,
  );
}

/**
 * Throws away a direct-to-S3 document upload that never got started, before
 * the caller falls back to the proxied upload -- otherwise the abandoned
 * attempt lingers beside the copy that does get indexed. Best-effort: never
 * throws (the backend's stale-job reaper hides and later deletes it anyway).
 *
 * @param {string} jobId - from /upload-file/document-init
 * @param {string|null} projectId
 */
export async function cancelDirectDocumentUpload(jobId, projectId) {
  const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  try {
    await authenticatedFetch(`${getApiBaseUrl()}/upload-file/document/${jobId}${query}`, { method: 'DELETE' });
  } catch (error) {
    console.warn('Could not clean up the unfinished direct upload:', error);
  }
}

/**
 * Upload a file directly into a project's library (not attached to any
 * particular conversation). A heavy PDF is indexed in the background exactly
 * like an in-conversation upload -- see .claude/plan/plan_pdfhandle.md -- so
 * this polls the same job-status endpoint until it completes.
 *
 * @param {string} projectId
 * @param {File} file
 * @param {(status: string) => void} [onStatus] - called with 'uploading' then 'indexing'
 * @returns {Promise<Object>} The completed upload result ({name, content, size, type, tokens, success})
 */
export async function uploadProjectFile(projectId, file, onStatus) {
  const apiBase = getApiBaseUrl();
  onStatus?.('uploading');

  let jobId = null;
  if (isEligibleForDirectUpload(file)) {
    try {
      jobId = await uploadProjectFileDirect(apiBase, projectId, file);
    } catch (directError) {
      if (directError.noFallback) throw directError;
      console.warn('Direct project file upload failed, falling back to proxied upload:', directError);
    }
  }

  if (!jobId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    const response = await authenticatedFetch(`${apiBase}/upload-file`, {
      method: 'POST',
      headers: {}, // let the browser set the multipart boundary
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || `Upload failed (status ${response.status})`);
    }

    const result = await response.json();
    if (response.status !== 202 || !result.job_id) {
      // Not large enough to need indexing -- already done.
      return result;
    }
    jobId = result.job_id;
  }

  onStatus?.('indexing');
  const deadline = Date.now() + PROJECT_UPLOAD_POLL_CEILING_MS;
  while (Date.now() < deadline) {
    const statusResponse = await authenticatedFetch(
      `${apiBase}/upload-file/jobs/${jobId}?project_id=${encodeURIComponent(projectId)}`,
      { method: 'GET' }
    );
    const data = await statusResponse.json().catch(() => null);
    if (!statusResponse.ok) {
      throw new Error(data?.detail || `Indexing failed (status ${statusResponse.status})`);
    }
    if (data?.success) return data;

    await new Promise((resolve) => setTimeout(resolve, PROJECT_UPLOAD_POLL_INTERVAL_MS));
  }
  throw new Error('Timed out waiting for the file to finish indexing.');
}
